import { ENV } from '@/constants';
import { exploreSchemaItemList } from '../exploreSchema';

export const schemaObject_AiAgeFilter = {
	'@context': 'https://schema.org',
	'@graph': [
		{
			'@type': 'WebPage',
			'@id': 'https://liftapp.ai/ai-age-filter',
			name: 'AI Age Filter — Face Old Age Effect Online',
			description:
				'Upload a portrait and preview realistic old age filter results in seconds. Choose from 6 age options, compare with your original, and regenerate instantly. Try Lift.',
			url: 'https://liftapp.ai/ai-age-filter',
			image: 'https://liftapp.ai/seo-image.webp',
			mainEntity: [
				{ '@id': 'https://liftapp.ai/ai-age-filter#product' },
				{ '@id': 'https://liftapp.ai/ai-age-filter#howto' },
				{ '@id': 'https://liftapp.ai/ai-age-filter#benefits' },
				{ '@id': 'https://liftapp.ai/ai-age-filter#faq' },
				{ '@id': 'https://liftapp.ai/ai-age-filter#why-lift' },
				{ '@id': 'https://liftapp.ai/ai-age-filter#explore-tools' },
				{ '@id': 'https://liftapp.ai/ai-age-filter#all-in-one' },
			],
		},
		// product
		{
			'@type': 'Product',
			'@id': 'https://liftapp.ai/ai-age-filter#product',
			name: 'AI Age Filter — Face Old Age Effect Online',
			description:
				'Upload a portrait and preview realistic old age filter results in seconds. Choose from 6 age options, compare with your original, and regenerate instantly. Try Lift.',
			url: 'https://liftapp.ai/ai-age-filter',
			brand: {
				'@type': 'Brand',
				name: 'Lift',
			},
			aggregateRating: {
				'@type': 'AggregateRating',
				ratingValue: 4.8,
				reviewCount: 130000,
				bestRating: '5',
				worstRating: '1',
			},
			interactionStatistic: [
				{
					'@type': 'InteractionCounter',
					interactionType: { '@type': 'InstallAction' },
					userInteractionCount: 13000000,
				},
			],
		},
		// howto
		{
			'@type': 'HowTo',
			'@id': 'https://liftapp.ai/ai-age-filter#howto',
			name: 'How it works',
			step: [
				{
					'@type': 'HowToStep',
					position: 1,
					name: 'Upload your photo',
					text: 'Choose a clear portrait from your device to start the age progression preview',
				},
				{
					'@type': 'HowToStep',
					position: 2,
					name: 'Choose an age',
					text: 'Pick one of 6 age options to see how your face changes over time',
				},
				{
					'@type': 'HowToStep',
					position: 3,
					name: 'Compare the result',
					text: 'View your original image next to the AI-generated age progression result',
				},
				{
					'@type': 'HowToStep',
					position: 4,
					name: 'Regenerate anytime',
					text: 'Try another age option and create a new version with one tap',
				},
			],
		},
		// benefit
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/ai-age-filter#benefits',
			name: 'Who can benefit from the AI Age Filter?',
			description:
				"Lift's AI Age Filter is useful for social media users, curious individuals, friends, content creators, and mobile users who want to preview how a face changes across different life stages.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Social media users can generate striking before-and-after posts with one portrait using the old age filter',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Curious users can explore how their face might look at different life stages using a simple aging filter',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Friends and entertainment seekers can apply the old face filter to group photos and compare results together',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Content creators can produce eye-catching visuals using an AI age progression effect in seconds',
				},
				{
					'@type': 'ListItem',
					position: 5,
					name: 'Mobile users can upload from their phone, select an age, and view results instantly on any device',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/benefits.3da6de53.webp',
				name: 'AI Age Progression for Social Media – Before and After Face Aging in Lift',
				description:
					'AI age progression comparison of a young woman aged to an older version, displayed in a social media post format with multiple age stage thumbnails in Lift',
			},
		},

		// faq
		{
			'@type': 'FAQPage',
			'@id': 'https://liftapp.ai/ai-age-filter#faq',
			mainEntity: [
				{
					'@type': 'Question',
					name: 'What is Lift AI Age Filter?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Lift AI Age Filter is a creative tool that transforms a single portrait into older-looking versions using artificial intelligence. It applies a natural-looking old age effect across multiple life stages, letting you visualize realistic face aging results from one photo.',
					},
				},
				{
					'@type': 'Question',
					name: 'How does Lift AI Age Filter work?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Upload a clear portrait, then select one of 6 age options from the Choose Age control. Lift processes the image and applies an AI-generated aging filter to your face. The result appears alongside your original photo so you can compare both versions directly.',
					},
				},
				{
					'@type': 'Question',
					name: 'Can I choose different age options?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Yes. Lift AI Age Filter includes 6 age options, so you can preview different older versions of the same portrait and compare the results easily.',
					},
				},
				{
					'@type': 'Question',
					name: 'What do I see after generation?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'After the result is ready, Lift shows your original photo alongside the generated age progression image, so you can compare both versions in one view.',
					},
				},
			],
		},

		// why lift
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/ai-age-filter#why-lift',
			name: "Why Lift's AI Age Filter stands out",
			description:
				"Lift's AI Age Filter combines professional-grade AI, ease of use, and affordability — making it a trusted choice for millions of users worldwide.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'High-quality results',
					description:
						'AI-powered technology delivers professional-grade visuals every time',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Intelligent rendering',
					description:
						'AI tailors the effect to the scene and subject for optimal results',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Cross-platform support',
					description: 'Available on iOS, Android, and Web for seamless access',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Budget-friendly',
					description:
						'Save on costly designers with an affordable and intuitive tool',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/whyUse.ae83a89c.webp',
				name: 'Lift AI Age Progression Tool – 6 Age Stages from Baby to Elder Age',
				description:
					'AI age progression result showing a young woman aged to mature version, with Lift app age selection UI displaying Baby, Child, Teenager, Adult, and Middle Age options',
			},
		},
		// explore
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/ai-age-filter#explore-tools',
			name: 'Explore the AI tools',
			description:
				'A curated list of AI-powered tools for image generation, enhancement, background removal, and more – all available on Lift.',
			itemListOrder: 'Unordered',
			...exploreSchemaItemList,
		},

		{
			'@type': 'SoftwareApplication',
			'@id': 'https://liftapp.ai/ai-age-filter#all-in-one',
			name: 'Try Lift – AI Content Creation Tools',
			description:
				'Your all-in-one tools to create, edit, and elevate your content. Create stunning content with AI tools, templates, and editing features effortlessly.',
			operatingSystem: 'iOS, Android, Web',
			applicationCategory: 'MultimediaApplication',
			url: `${ENV.LIFT_WEB_APP_URL}/sign-in/?auto_login=true`,
			applicationSubCategory: 'Content Creation',
			publisher: {
				'@type': 'Organization',
				name: 'Lift',
				url: 'https://liftapp.ai',
			},
			potentialAction: [
				{
					'@type': 'ViewAction',
					name: 'Open Web App',
					target: `${ENV.LIFT_WEB_APP_URL}/sign-in/?auto_login=true`,
				},
			],
		},

		{
			'@type': 'BreadcrumbList',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Home',
					item: 'https://liftapp.ai',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'AI Age Filter',
					item: 'https://liftapp.ai/ai-age-filter',
				},
			],
		},
	],
};
